// Operator resolution of external effects with an unknown outcome (decision D41-B2, PRD AC08). A lost executor's effect
// is never replayed on "no evidence": it stays `unknown` until a person who checked the remote system records what
// happened. `done` settles it with the evidence the person names; `not_done` re-arms it so the next run performs it once.
import { z } from "zod";

const id = z.uuid();

export const EXTERNAL_OPERATION_RESOLUTIONS = ["done", "not_done"] as const;
export type ExternalOperationResolution = (typeof EXTERNAL_OPERATION_RESOLUTIONS)[number];

/**
 * The kinds whose effects run through the engine's fenced effect runner, which reads the operator's decision (reconcile
 * of a `done`, single consumption of a `not_done`). Other kinds keep their own recovery and are neither listed nor
 * resolvable here. The engine test `effects-unknown-never-replayed` keeps this list equal to the runner's call sites.
 */
export const RESOLVABLE_EXTERNAL_OPERATION_KINDS = [
  "git.commit",
  "git.push",
  "git.push.publish",
  "git.integrate",
  "pr.create",
  "github.repository.create",
  "github.deploy_key.add",
  "coolify.app.create",
  "coolify.key.create",
  "coolify.deploy",
] as const;
export type ResolvableExternalOperationKind = (typeof RESOLVABLE_EXTERNAL_OPERATION_KINDS)[number];
export const isResolvableExternalOperationKind = (kind: string): kind is ResolvableExternalOperationKind =>
  (RESOLVABLE_EXTERNAL_OPERATION_KINDS as readonly string[]).includes(kind);

/** GET /v1/projects/{id}/external-operations?state=unknown: what is waiting for a person (no request payloads). */
export const externalOperationViewSchema = z.object({
  id,
  kind: z.string(),
  state: z.enum(["pending", "succeeded", "failed", "unknown"]),
  task_id: id.nullable(),
  /** Where the effect lands when the caller recorded it (e.g. `owner/repo@branch`). */
  destination: z.string().nullable(),
  external_ref: z.string().nullable(),
  created_at: z.iso.datetime(),
});
export type ExternalOperationView = z.infer<typeof externalOperationViewSchema>;

/** POST /v1/external-operations/{id}/resolution: organization admin only, confirmed, audited. */
export const externalOperationResolutionRequestSchema = z.object({
  outcome: z.enum(EXTERNAL_OPERATION_RESOLUTIONS),
  /** What the person checked on the remote system (required; never inferred). */
  note: z.string().trim().min(1).max(500),
  /** done: the provider's identifier of the effect (PR number, commit, deployment), when known. */
  external_ref: z.string().trim().min(1).max(300).optional(),
  confirmed: z.literal(true),
});
export type ExternalOperationResolutionRequest = z.infer<typeof externalOperationResolutionRequestSchema>;

/** POST /v1/server-operations/{id}/resolution: the same confirmed decision; a server operation's receipt is its own, so no reference. */
export const serverOperationResolutionRequestSchema = externalOperationResolutionRequestSchema.omit({ external_ref: true }).strict();
export type ServerOperationResolutionRequest = z.infer<typeof serverOperationResolutionRequestSchema>;
